import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ChevronLeft, ChevronRight, Star, CheckCircle, Shield, Truck, Phone, Award, TrendingUp, ExternalLink, CheckCircle2 } from 'lucide-react';
import api from '../utils/api';
import { addToCart } from '../utils/cart';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { toast } from 'sonner';
import BentoCategoryGrid from '../components/BentoCategoryGrid';
import RitualCarousel from '../components/RitualCarousel';
import ReelsSection from '../components/ReelsSection';
import { openWhatsAppInquiry } from '../utils/inquiry';
import CustomerShowcase from '@/components/Customershowcase';
import FeaturedProductCard from '@/components/Featuredproductcard';
 
// ── Review data ──────────────────────────────────────────────────────────────
const REVIEWS = [
  {
    id: 1,
    stars: 5,
    text: 'Owner and staff are very polite and friendly. Any alteration requests are also dealt with in a good manner.',
    name: 'Zeeshan MW',
    city: 'Hubli',
    initials: 'ZM',
    avatarBg: 'bg-amber-50',
    avatarText: 'text-amber-700',
  },
  {
    id: 2,
    stars: 5,
    text: 'Beautiful trendy sarees. The staff is well trained to understand customer requirements. Wide range of sarees, chudidar sets and readymade collections. Quality and unique collections cannot be replaced.',
    name: 'Parvati Balaraddi',
    city: 'Hubli',
    initials: 'PB',
    avatarBg: 'bg-emerald-50',
    avatarText: 'text-emerald-700',
  },
  {
    id: 3,
    stars: 4,
    text: "Beautiful and classic designs. Party wear clothes are superb and affordable. Very generous people — just let them know what you're looking for and they'll find it.",
    name: 'Jayashree Hiremath',
    city: 'Hubli',
    initials: 'JH',
    avatarBg: 'bg-pink-50',
    avatarText: 'text-pink-700',
  },
];
 
const StarRow = ({ count, size = 'w-4 h-4' }) => (
  <div className="flex items-center gap-0.5">
    {[...Array(5)].map((_, i) => (
      <Star
        key={i}
        className={`${size} ${i < count ? 'text-[#E5A93C] fill-[#E5A93C]' : 'text-gray-200 fill-gray-200'}`}
      />
    ))}
  </div>
);
// ─────────────────────────────────────────────────────────────────────────────
 
const HomePage = () => {
  const [heroSlides, setHeroSlides] = useState([]);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [faqs, setFaqs] = useState([]);
  const [videoUrls, setVideoUrls] = useState({ video1: '', video2: '', video3: '' });
  const navigate = useNavigate();
  const carouselRef = useRef(null);
 
  useEffect(() => {
    fetchHeroSlides();
    fetchFeaturedProducts();
    fetchCategories();
    fetchTestimonials();
    fetchFAQs();
    fetchVideoUrls();
  }, []);
 
  useEffect(() => {
    if (heroSlides.length === 0) return;
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [heroSlides.length]);
 
  useEffect(() => {
    const interval = setInterval(() => {
      if (carouselRef.current) {
        carouselRef.current.scrollLeft += 2;
        if (carouselRef.current.scrollLeft >= carouselRef.current.scrollWidth / 2) {
          carouselRef.current.scrollLeft = 0;
        }
      }
    }, 20);
    return () => clearInterval(interval);
  }, []);
 
  const fetchHeroSlides = async () => {
    try {
      const response = await api.get('/hero-slides');
      if (response.data.length > 0) {
        setHeroSlides(response.data);
      } else {
        setHeroSlides([{
          image: 'https://images.unsplash.com/photo-1654764746225-e63f5e90facd',
          headline: 'Premium Ethnic Wear Collection',
          subheading: 'Discover timeless elegance in every thread',
          cta1_text: 'Explore Collections',
          cta1_link: '/collections',
          cta2_text: 'WhatsApp Us',
          cta2_link: 'https://wa.me/919538769669'
        }]);
      }
    } catch (error) { console.error('Error fetching hero slides:', error); }
  };
 
  const fetchFeaturedProducts = async () => {
    try {
      const response = await api.get('/products?featured=true&limit=10');
      setFeaturedProducts(response.data);
    } catch (error) { console.error('Error fetching featured products:', error); }
  };
 
  const fetchCategories = async () => {
    try {
      const response = await api.get('/categories?visible=true');
      setCategories(response.data);
    } catch (error) { console.error('Error fetching categories:', error); }
  };
 
  const fetchTestimonials = async () => {
    try {
      const response = await api.get('/testimonials');
      setTestimonials(response.data.length > 0 ? response.data : [
        { name: 'Priya Sharma', city: 'Hubli', review: 'Amazing collection and excellent service!', rating: 5 },
        { name: 'Anita Desai', city: 'Bangalore', review: 'Quality is outstanding.', rating: 5 },
        { name: 'Meera Patel', city: 'Mumbai', review: 'Beautiful ethnic wear at great prices.', rating: 5 }
      ]);
    } catch (error) { console.error('Error fetching testimonials:', error); }
  };
 
  const fetchFAQs = async () => {
    try {
      const response = await api.get('/faqs?category=general');
      setFaqs(response.data.length > 0 ? response.data : [
        { question: 'How do I place an order?', answer: 'Browse collections and use the WhatsApp inquiry button.' },
        { question: 'Do you deliver outside Karnataka?', answer: 'Yes! We deliver pan-India.' }
      ]);
    } catch (error) { console.error('Error fetching FAQs:', error); }
  };
 
  const fetchVideoUrls = async () => {
    try {
      const [v1, v2, v3] = await Promise.all([
        api.get('/settings/video1_url'),
        api.get('/settings/video2_url'),
        api.get('/settings/video3_url')
      ]);
      setVideoUrls({ video1: v1.data.value, video2: v2.data.value, video3: v3.data.value });
    } catch (error) { console.error('Error fetching video URLs:', error); }
  };
 
  const handleAddToCart = (product) => {
    addToCart(product);
    window.dispatchEvent(new Event('cartUpdated'));
    toast.success('Added to cart!');
  };
 
  const nextSlide = () => setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
  const prevSlide = () => setCurrentSlide((prev) => (prev - 1 + heroSlides.length) % heroSlides.length);
 
  return (
    <div className="min-h-screen">
 
      {/* ── HERO SLIDER ─────────────────────────────────────────────────────── */}
      <section className="relative w-full overflow-hidden" data-testid="hero-section">
        {heroSlides.map((slide, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0 }}
            animate={{ opacity: index === currentSlide ? 1 : 0 }}
            transition={{ duration: 0.8 }}
            className="absolute inset-0 w-full h-full"
            style={{ pointerEvents: index === currentSlide ? 'auto' : 'none' }}
          >
            {/* Full image — no cropping, scales to natural height */}
            <img
              src={slide.image}
              alt={slide.headline}
              className="w-full h-full object-cover object-top"
              style={{ display: 'block' }}
            />
            {/* Overlay */}
            <div className="absolute inset-0 bg-gradient-to-r from-black/55 via-black/20 to-transparent" />
 
            {/* Text content */}
            <div className="absolute inset-0 flex items-center">
              <div className="container mx-auto px-4 md:px-8">
                <div className="max-w-2xl text-white">
                  <motion.h1
                    initial={{ y: 30, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: 0.2 }}
                    className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold mb-4 tracking-tight leading-none"
                    data-testid="hero-headline"
                  >
                    {slide.headline}
                  </motion.h1>
                  <motion.p
                    initial={{ y: 30, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: 0.4 }}
                    className="text-base md:text-lg mb-8 leading-relaxed"
                  >
                    {slide.subheading}
                  </motion.p>
                  <motion.div
                    initial={{ y: 30, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: 0.6 }}
                    className="flex flex-wrap gap-4"
                  >
                    <Link to={slide.cta1_link} className="btn-primary" data-testid="hero-cta1">
                      {slide.cta1_text}
                    </Link>
                    {slide.cta2_text && (
                      <a
                        href={slide.cta2_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn-outline bg-white/10 backdrop-blur-sm text-white border-white hover:bg-white hover:text-[#2A0000]"
                        data-testid="hero-cta2"
                      >
                        {slide.cta2_text}
                      </a>
                    )}
                  </motion.div>
                </div>
              </div>
            </div>
          </motion.div>
        ))}
 
        {/*
          Invisible spacer image — uses the current slide's natural dimensions
          to size the section correctly so the full image is visible.
          opacity-0 so it's invisible but still pushes the section height.
        */}
        {heroSlides.length > 0 && (
          <img
            key={currentSlide}
            src={heroSlides[currentSlide].image}
            alt=""
            aria-hidden="true"
            className="w-full opacity-0 pointer-events-none select-none"
            style={{ display: 'block', minHeight: '50vh' }}
          />
        )}
 
        {/* Dot nav */}
        {heroSlides.length > 1 && (
          <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10">
            <div className="bg-black/50 backdrop-blur-md rounded-full px-6 py-3 flex items-center space-x-3">
              {heroSlides.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentSlide(index)}
                  className={`transition-all duration-300 ${
                    index === currentSlide
                      ? 'w-8 h-2 bg-white rounded-full'
                      : 'w-2 h-2 bg-white/40 hover:bg-white/70 rounded-full'
                  }`}
                  data-testid={`hero-dot-${index}`}
                />
              ))}
            </div>
          </div>
        )}
      </section>

      <CustomerShowcase />

      {/* REELS SECTION */}
      <ReelsSection />

      {/* SHOP BY CATEGORIES - BENTO GRID */}
      <BentoCategoryGrid />

      {/* RITUAL STAGGERED CAROUSEL */}
      <RitualCarousel />

      {/* SECTION 3: Auto-Scrolling Featured Products */}
      {featuredProducts.length > 0 && (
  <section
    className="py-16 md:py-20 overflow-hidden"
    style={{ background: '#FFFDF9' }}
    data-testid="featured-products-section"
  >
    {/* Header */}
    <div className="container mx-auto px-4 md:px-8 mb-10">
      <div className="flex items-end justify-between">
        <div>
          <p className="text-xs font-bold tracking-[0.25em] uppercase text-[#E5A93C] mb-2">
            Handpicked For You
          </p>
          <h2 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-[#2A0000] leading-none">
            Featured Collection
          </h2>
        </div>
        <div className="hidden md:block flex-1 h-px bg-gradient-to-r from-transparent via-[#E5A93C]/30 to-transparent mx-8 mb-2" />
        <Link
          to="/collections"
          className="hidden md:inline-flex items-center gap-1.5 text-sm font-semibold text-[#2A0000] hover:text-[#E5A93C] transition-colors mb-2"
        >
          View All
          <ChevronRight className="w-4 h-4" />
        </Link>
      </div>
    </div>
 
    {/* Carousel — only duplicate array when 4+ products exist for seamless loop */}
    <div
      ref={carouselRef}
      className="flex gap-5 overflow-x-scroll scrollbar-hide px-4 md:px-8"
      style={{ scrollBehavior: 'auto' }}
    >
      {(featuredProducts.length >= 4
        ? [...featuredProducts, ...featuredProducts]
        : featuredProducts
      ).map((product, index) => (
        <FeaturedProductCard
          key={`${product.sku}-${index}`}
          product={product}
          index={index}
          onAddToCart={handleAddToCart}
        />
      ))}
    </div>
 
    {/* Mobile "view all" */}
    <div className="mt-8 text-center md:hidden">
      <Link to="/collections" className="btn-primary inline-flex items-center gap-2">
        Browse All Collections
        <ChevronRight className="w-4 h-4" />
      </Link>
    </div>
  </section>
)}



      {/* SECTION 5: Brand Heritage Spotlight */}
      <section className="py-16 md:py-24 bg-white" data-testid="heritage-section">
        <div className="container mx-auto px-4 md:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-[#2A0000] mb-6">Our Heritage Since 2014</h2>
              <p className="text-base leading-relaxed text-[#2C2C2C] mb-4">
                Founded in 2014 with roots in Ahmedabad, Gujarat's rich textile heritage, Ahmedabad NX has grown into Hubli's most trusted destination for premium ladies ethnic wear.
              </p>
              <p className="text-base leading-relaxed text-[#2C2C2C] mb-6">
                We specialize in handpicked collections of sarees, gowns, chudidhar sets, and designer pieces that celebrate the elegance of Indian craftsmanship.
              </p>
              <div className="space-y-4 mb-8">
                {[
                  { year: '2014', label: 'Founded', sub: 'Started our journey in Hubli' },
                  { year: '500+', label: 'Collections', sub: 'Curated designer pieces' },
                  { year: '1000+', label: 'Happy Brides', sub: 'Trusted by brides across India' },
                ].map(({ year, label, sub }) => (
                  <div key={label} className="flex items-center space-x-4">
                    <div className="w-16 h-16 bg-[#E5A93C]/10 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="font-heading text-xl font-bold text-[#E5A93C]">{year}</span>
                    </div>
                    <div>
                      <h4 className="font-semibold text-[#2A0000]">{label}</h4>
                      <p className="text-sm text-gray-600">{sub}</p>
                    </div>
                  </div>
                ))}
              </div>
              <Link to="/about" className="btn-primary">Discover Our Story</Link>
            </div>
            <div>
              <img src="https://res.cloudinary.com/dhobeyvjt/image/upload/v1779696902/unnamed_4_zixcwo.webp"
                alt="Ahmedabad NX Showroom" className="w-full rounded-sm shadow-xl" />
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 6: Video Reels Showcase */}
      {(videoUrls.video1 || videoUrls.video2 || videoUrls.video3) && (
        <section className="py-16 md:py-24 bg-[#FFFDF9]" data-testid="video-section">
          <div className="container mx-auto px-4 md:px-8">
            <h2 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-[#2A0000] text-center mb-12">See the Magic in Motion</h2>
            <div className="grid md:grid-cols-3 gap-6">
              {videoUrls.video1 && (
                <div className="aspect-video bg-gray-200 rounded-sm overflow-hidden">
                  <iframe src={videoUrls.video1} title="Video 1" className="w-full h-full"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
                </div>
              )}
              {videoUrls.video2 && (
                <div className="aspect-video bg-gray-200 rounded-sm overflow-hidden">
                  <iframe src={videoUrls.video2} title="Video 2" className="w-full h-full"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
                </div>
              )}
              {videoUrls.video3 && (
                <div className="aspect-video bg-gray-200 rounded-sm overflow-hidden">
                  <iframe src={videoUrls.video3} title="Video 3" className="w-full h-full"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* SECTION 7: Google Reviews — redesigned */}
      <section className="py-16 md:py-24 bg-white" data-testid="reviews-section">
        <div className="container mx-auto px-4 md:px-8">

          {/* Hero rating */}
          <div className="text-center mb-12">
            <div className="flex items-center justify-center gap-2 mb-3">
              <StarRow count={4} size="w-5 h-5" />
              <span className="text-3xl font-bold text-[#2A0000] leading-none">4.0</span>
              <span className="text-gray-400 text-lg">/&nbsp;5</span>
            </div>
            <p className="text-sm text-gray-500 mb-3">Based on 199+ verified Google reviews</p>
            <a
              href="https://www.google.com/search?sca_esv=5ddb32e5f5a71fbf&rlz=1C1CHBD_enIN1086IN1087&sxsrf=ANbL-n4A1CYCB-qYDY0I_sGUt2g3j72fFA:1779504500189&si=AL3DRZEsmMGCryMMFSHJ3StBhOdZ2-6yYkXd_doETEE1OR-qObwElQwJbtzy8rqNvcG1xWi1nkDcmWJaBfPavI_f8zoJKKw0tXnFpkaGLa8Gr6bG11HIBwAk2InWfX5pyRkvImppXDOZ&q=Ahmedabad+Nx+Reviews&sa=X&ved=2ahUKEwjur_Dsss6UAxUORmwGHX46HfIQ0bkNegQILhAF&biw=1707&bih=820&dpr=1.13"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-[#E5A93C] hover:underline text-sm font-medium"
            >
              See all reviews on Google
              <ExternalLink className="w-3.5 h-3.5" />
            </a>
            {/* Gold accent line */}
            <div className="mx-auto mt-5 w-12 h-0.5 rounded-full bg-[#E5A93C]" />
          </div>

          {/* Review cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {REVIEWS.map((review) => (
              <div
                key={review.id}
                className="group bg-white border border-gray-100 rounded-2xl p-6 flex flex-col gap-4 hover:border-[#E5A93C]/40 hover:shadow-sm transition-all duration-200"
              >
                {/* Stars + verified */}
                <div className="flex items-center justify-between">
                  <StarRow count={review.stars} />
                  <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-1 rounded-full bg-amber-50 text-amber-700">
                    <CheckCircle2 className="w-3 h-3" />
                    Verified
                  </span>
                </div>

                {/* Quote */}
                <div className="flex-1">
                  <span className="block text-3xl leading-none text-[#E5A93C] font-serif mb-1" aria-hidden="true">"</span>
                  <p className="text-sm text-gray-600 leading-relaxed">{review.text}</p>
                </div>

                {/* Reviewer */}
                <div className="flex items-center gap-3 pt-2 border-t border-gray-100">
                  <div className={`w-9 h-9 rounded-full flex items-center justify-center text-xs font-semibold flex-shrink-0 ${review.avatarBg} ${review.avatarText}`}>
                    {review.initials}
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-[#2A0000]">{review.name}</p>
                    <p className="text-xs text-gray-400">{review.city}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 8: USP Tiles */}
      <section className="py-16 md:py-24 bg-[#FFFDF9]" data-testid="usp-section">
        <div className="container mx-auto px-4 md:px-8">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6">
            {[
              { Icon: Award,       label: 'Premium Quality' },
              { Icon: Phone,       label: 'WhatsApp Ordering' },
              { Icon: CheckCircle, label: 'Authentic Fabrics' },
              { Icon: Shield,      label: 'Trusted Brand' },
              { Icon: TrendingUp,  label: 'Bridal Specialists' },
            ].map(({ Icon, label }) => (
              <div key={label} className="text-center p-6 bg-white rounded-sm hover:bg-[#E5A93C]/10 transition-colors group">
                <div className="w-12 h-12 bg-[#E5A93C]/10 group-hover:bg-[#E5A93C] rounded-full flex items-center justify-center mx-auto mb-4 transition-colors">
                  <Icon className="w-6 h-6 text-[#E5A93C] group-hover:text-white" />
                </div>
                <h4 className="font-semibold text-sm text-[#2C2C2C]">{label}</h4>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 10: FAQ Accordion */}
      <section className="py-16 md:py-24 bg-[#FFFDF9]" data-testid="faq-section">
        <div className="container mx-auto px-4 md:px-8 max-w-3xl">
          <h2 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-[#2A0000] text-center mb-12">Frequently Asked Questions</h2>
          <Accordion type="single" collapsible className="space-y-4">
            {faqs.map((faq, index) => (
              <AccordionItem key={index} value={`item-${index}`} className="bg-white border border-[#E5A93C]/20 rounded-sm px-6">
                <AccordionTrigger className="text-left font-semibold text-[#2A0000] hover:text-[#E5A93C]">{faq.question}</AccordionTrigger>
                <AccordionContent className="text-[#2C2C2C] leading-relaxed">{faq.answer}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* SECTION 11: WhatsApp Subscribe */}
      <section className="py-16 md:py-24 bg-[#2A0000] text-white" data-testid="newsletter-section">
        <div className="container mx-auto px-4 md:px-8 text-center max-w-2xl">
          <h2 className="font-heading text-2xl md:text-3xl font-bold mb-4">Stay Connected</h2>
          <p className="mb-8 text-white/80">Join our WhatsApp broadcast for exclusive offers and new arrivals</p>
          <a
            href="https://wa.me/917760591999?text=I%20would%20like%20to%20join%20your%20WhatsApp%20broadcast%20list"
            target="_blank" rel="noopener noreferrer"
            className="bg-[#25D366] hover:bg-[#20BA5A] text-white px-8 py-3 rounded-sm transition-colors font-medium inline-block"
            data-testid="whatsapp-subscribe"
          >
            Join WhatsApp Broadcast
          </a>
        </div>
      </section>

    </div>
  );
};

export default HomePage;